import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import useAnalytics from '../../hooks/useAnalytics';
import SEO from '../../components/SEO/SEO';

const Register = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    fechaNacimiento: '',
    telefono: '',
    password: '',
    confirmPassword: '',
    fotoPerfil: null
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const { signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const { trackSignUp, trackFormSubmit, trackError } = useAnalytics();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Limpiar error cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de archivo
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, fotoPerfil: 'Solo se permiten archivos de imagen (JPG, PNG, GIF)' }));
        return;
      }
      
      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, fotoPerfil: 'La imagen no puede ser mayor a 5MB' }));
        return;
      }
      
      setFormData(prev => ({ ...prev, fotoPerfil: file }));
      
      // Crear preview de la imagen
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Limpiar errores
      if (errors.fotoPerfil) {
        setErrors(prev => ({ ...prev, fotoPerfil: '' }));
      }
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, fotoPerfil: null }));
    setPreviewImage(null);
    // Limpiar el input file
    const fileInput = document.getElementById('fotoPerfil');
    if (fileInput) fileInput.value = '';
  };

  const validateForm = () => {
    const newErrors = {};

    // Validar nombre
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    } else if (formData.nombre.trim().length < 2) {
      newErrors.nombre = 'El nombre debe tener al menos 2 caracteres';
    }

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Ingresa un correo electrónico válido';
    }

    // Validar fecha de nacimiento
    if (!formData.fechaNacimiento) {
      newErrors.fechaNacimiento = 'La fecha de nacimiento es requerida';
    } else {
      const today = new Date();
      const birthDate = new Date(formData.fechaNacimiento);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      if (age < 18) {
        newErrors.fechaNacimiento = 'Debes ser mayor de 18 años';
      }
    }

    // Validar teléfono
    const phoneRegex = /^[0-9]{9,15}$/;
    if (!formData.telefono) {
      newErrors.telefono = 'El número de teléfono es requerido';
    } else if (!phoneRegex.test(formData.telefono.replace(/\s/g, ''))) {
      newErrors.telefono = 'Ingresa un número de teléfono válido';
    }

    // Validar contraseña
    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    // Validar confirmación de contraseña
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirma tu contraseña';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGoogleRegister = async (credentialResponse) => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const { data, error } = await signInWithGoogle(credentialResponse);
      
      if (error) {
        setError(error);
        trackError('google_register_failed', error, '/register');
        return;
      }
      
      // Tracking de registro exitoso con Google
      trackSignUp(data?.id || 'google_user', 'google');
      
      setSuccess('¡Registro exitoso con Google! Redirigiendo...');
      
      // Redirigir después de un breve delay
      setTimeout(() => {
        navigate('/');
      }, 1500);
      
    } catch (error) {
      setError('Error al registrarse con Google. Inténtalo de nuevo.');
      trackError('google_register_error', error.message, '/register');
    } finally {
      setLoading(false);
    }
  };

  const uploadProfileImage = async (userId, file) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `profile_${userId}_${Date.now()}.${fileExt}`;
      const filePath = `profiles/${fileName}`;
  
      const { error: uploadError } = await supabase.storage
        .from('product-images') // Cambiar de 'profile-images' a 'product-images'
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });
  
      if (uploadError) throw uploadError;
  
      // Obtener URL pública
      const { data: { publicUrl } } = supabase.storage
        .from('product-images') // Cambiar de 'profile-images' a 'product-images'
        .getPublicUrl(filePath);
  
      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const getAuthEndpoint = () => {
    const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    return isLocal ? 'http://localhost:3001/api/auth' : '/.netlify/functions/auth';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      trackFormSubmit('register_form', false, 'validation_failed');
      return;
    }
    
    trackFormSubmit('register_form', true, 'validation_passed');

    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const res = await fetch(`${getAuthEndpoint()}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: formData.nombre.trim(),
          email: formData.email.toLowerCase().trim(),
          password: formData.password,
          telefono: formData.telefono.trim(),
          fecha_nacimiento: formData.fechaNacimiento
        })
      });
      const data = await res.json();

      if (!data.success) {
        setError(data.error || 'Error al crear la cuenta');
        trackError('register_error', data.error, '/register');
        setLoading(false);
        return;
      }

      const newUser = data.user;

      try {
        const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
        const startEndpoint = isLocal ? 'http://localhost:3001/api/verify-email-start' : '/.netlify/functions/start-verify-email';
        await fetch(startEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: newUser.email, nombre: newUser.nombre })
        });
      } catch (e) {}

      let profileImageUrl = null;
      if (formData.fotoPerfil) {
        try {
          profileImageUrl = await uploadProfileImage(newUser.id, formData.fotoPerfil);
          const { error: updateError } = await supabase
            .from('Users')
            .update({ profile_image: profileImageUrl })
            .eq('id', newUser.id);

          if (updateError) {
            console.warn('Error actualizando imagen de perfil:', updateError);
          }
        } catch (imageError) {
          console.warn('Error subiendo imagen de perfil:', imageError);
        }
      }

      setSuccess('¡Registro exitoso! Te enviamos un correo para verificar tu cuenta.');
      trackSignUp(newUser.id, 'email');
      
      const loginResult = await signIn(formData.email, formData.password);
      
      if (loginResult.error) {
        setSuccess('Registro exitoso. Por favor, inicia sesión manualmente.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setTimeout(() => {
          navigate('/');
        }, 1500);
      }
      
    } catch (error) {
      console.error('Error en registro:', error);
      setError(`Error al crear la cuenta: ${error.message}`);
      trackError('register_general_error', error.message, '/register');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Crear Cuenta"
        description="Regístrate en Resmoke, el marketplace de confianza para comprar y vender productos. Crea tu cuenta gratis y empieza a vender hoy."
        url="/register"
      />
      <div className="flex flex-col lg:flex-row min-h-screen">
      {/* Imagen izquierda */}
      <div className="hidden lg:block lg:w-1/2">
        <img
          src="/img/resmoke/LoginRegister.png"
          alt="hookah"
          className="h-full w-full object-cover"
        />
      </div>

      {/* Formulario */}
      <div className="flex-1 lg:w-1/2 flex items-center justify-center p-4 lg:p-0">
        <form onSubmit={handleSubmit} className="w-full max-w-md px-4 sm:px-6 space-y-3 sm:space-y-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center">Registro</h2>

          {error && (
            <div className="text-red-500 text-sm text-center bg-red-50 p-3 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="text-green-500 text-sm text-center bg-green-50 p-3 rounded-lg">
              {success}
            </div>
          )}

          <div>
            <input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              placeholder="Nombre"
              className={`w-full border px-4 py-3 sm:py-2 rounded-lg outline-none focus:ring-2 focus:ring-black transition-all ${
                errors.nombre ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.nombre && <p className="text-red-500 text-xs mt-1">{errors.nombre}</p>}
          </div>

          <div>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Correo Electrónico"
              className={`w-full border px-4 py-3 sm:py-2 rounded-lg outline-none focus:ring-2 focus:ring-black transition-all ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          <div>
            <input
              type="date"
              name="fechaNacimiento"
              value={formData.fechaNacimiento}
              onChange={handleChange}
              placeholder="dd/mm/aaaa"
              className={`w-full border px-4 py-3 sm:py-2 rounded-lg text-gray-500 outline-none focus:ring-2 focus:ring-black transition-all ${
                errors.fechaNacimiento ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.fechaNacimiento && <p className="text-red-500 text-xs mt-1">{errors.fechaNacimiento}</p>}
          </div>

          <div>
            <input
              type="tel"
              name="telefono"
              value={formData.telefono}
              onChange={handleChange}
              placeholder="Número de teléfono"
              className={`w-full border px-4 py-3 sm:py-2 rounded-lg outline-none focus:ring-2 focus:ring-black transition-all ${
                errors.telefono ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.telefono && <p className="text-red-500 text-xs mt-1">{errors.telefono}</p>}
          </div>

          <div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Contraseña"
                className={`w-full border px-4 pr-12 py-3 sm:py-2 rounded-lg outline-none focus:ring-2 focus:ring-black transition-all ${
                  errors.password ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.733 5.076A10.744 10.744 0 0 1 12 5c7 0 10 7 10 7a13.132 13.132 0 0 1-1.67 2.68" />
                    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                    <path d="M14.12 14.12a3 3 0 0 1-4.24-4.24" />
                    <path d="M1 1l22 22" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                    <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                  </svg>
                )}
              </button>
            </div>
            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
          </div>

          <div>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirmar Contraseña"
                className={`w-full border px-4 pr-12 py-3 sm:py-2 rounded-lg outline-none focus:ring-2 focus:ring-black transition-all ${
                  errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                aria-label={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.733 5.076A10.744 10.744 0 0 1 12 5c7 0 10 7 10 7a13.132 13.132 0 0 1-1.67 2.68" />
                    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                    <path d="M14.12 14.12a3 3 0 0 1-4.24-4.24" />
                    <path d="M1 1l22 22" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                    <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                  </svg>
                )}
              </button>
            </div>
            {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
          </div>

          {/* Sección para foto de perfil */}
          <div className="space-y-3">
            <label className="block text-sm sm:text-base font-medium text-gray-700">
              Foto de Perfil (Opcional)
            </label>
            
            <div className="flex items-center justify-center">
              <div className="relative">
                {previewImage ? (
                  <div className="relative">
                    <img
                      src={previewImage}
                      alt="Preview"
                      className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-2 border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center text-xs hover:bg-red-600 transition-all"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      id="fotoPerfil"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors">
                      <div className="text-center">
                        <div className="text-gray-400 text-xs sm:text-sm font-medium text-center">
                          Foto de Perfil
                        </div>
                      </div>
                    </div>
                  </label>
                )}
              </div>
            </div>
            
            {errors.fotoPerfil && <p className="text-red-500 text-xs mt-1">{errors.fotoPerfil}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 sm:py-2 rounded-lg transition-all transform hover:scale-105 ${
              loading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-black hover:bg-gray-800'
            } text-white font-medium`}
          >
            {loading ? 'Registrando...' : 'Registrarse'}
          </button>

          {/* Separador */}
          <div className="flex items-center my-4">
            <div className="flex-1 border-t border-gray-300"></div>
            <span className="px-4 text-gray-500 text-sm">o</span>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>

          {/* Botón de Google OAuth */}
          <div className="w-full">
            <GoogleLogin
              onSuccess={handleGoogleRegister}
              onError={() => {
                setError('Error al conectar con Google. Inténtalo de nuevo.');
              }}
              useOneTap={false}
              theme="outline"
              size="large"
              width="100%"
              text="signup_with"
              shape="rectangular"
            />
          </div>

          <div className="text-center text-sm sm:text-base text-gray-600">
            ¿Ya estás registrado?{' '}
            <a href="/login" className="text-orange-600 hover:underline transition-colors">Inicia Sesión</a>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default Register;
